let _enabled = null;
let _seq = 0;

function isEnabled() {
  if (_enabled !== null) return _enabled;
  try {
    _enabled = localStorage.getItem("w86_stream_debug") === "1";
  } catch {
    _enabled = false;
  }
  return _enabled;
}

function seq() {
  return ++_seq;
}

function ts() {
  return new Date().toISOString().slice(11, 23);
}

function summarizeMessages(messages) {
  if (!Array.isArray(messages)) return "null";
  return messages.map((m, i) => {
    const id = m.messageId || m.id || "?";
    const role = m.role || "?";
    const opt = m._optimistic ? " [OPT]" : "";
    const status = m.status ? ` (${m.status})` : "";
    const partsCount = Array.isArray(m.parts) ? ` parts=${m.parts.length}` : "";
    const contentSnip = m.content ? ` "${m.content.slice(0, 40)}${m.content.length > 40 ? "..." : ""}"` : "";
    return `  [${i}] ${role}${opt}${status} id=${id}${partsCount}${contentSnip}`;
  }).join("\n");
}

function summarizeParts(parts) {
  if (!Array.isArray(parts)) return "none";
  return parts.map((p, i) => {
    if (p.type === "text") {
      const len = (p.text || "").length;
      return `[${i}] text state=${p.state} len=${len}`;
    }
    if (p.type === "tool") {
      return `[${i}] tool=${p.toolName} callId=${p.toolCallId} state=${p.state}`;
    }
    return `[${i}] ${p.type || "unknown"}`;
  }).join(", ");
}

const INCOMPLETE_TOOL_STATES = new Set(["input-streaming", "input-available"]);

function findIncompleteToolParts(messages) {
  if (!Array.isArray(messages)) return [];
  const stuck = [];
  for (const msg of messages) {
    const messageId = msg.messageId || msg.id || "?";
    const msgStatus = msg.status || "?";
    const parts = msg.parts || [];
    for (let partIndex = 0; partIndex < parts.length; partIndex++) {
      const part = parts[partIndex];
      if (part.type !== "tool") continue;
      const state = part.state || "?";
      if (!INCOMPLETE_TOOL_STATES.has(state)) continue;
      stuck.push({
        messageId,
        msgStatus,
        partIndex,
        toolCallId: part.toolCallId || "?",
        toolName: part.toolName || "?",
        state,
      });
    }
  }
  return stuck;
}

function summarizeIncompleteTools(messages) {
  const stuck = findIncompleteToolParts(messages);
  if (stuck.length === 0) return "none";
  return stuck
    .map(
      (t) =>
        `${t.toolName}(${t.toolCallId}) state=${t.state} msg=${t.messageId} msgStatus=${t.msgStatus}`
    )
    .join("; ");
}

function diffMessages(label, before, after) {
  if (!isEnabled()) return;
  const beforeIds = new Set((before || []).map(m => m.messageId || m.id || "?"));
  const afterIds = new Set((after || []).map(m => m.messageId || m.id || "?"));
  const added = [...afterIds].filter(id => !beforeIds.has(id));
  const removed = [...beforeIds].filter(id => !afterIds.has(id));
  if (added.length || removed.length || (before || []).length !== (after || []).length) {
    console.log(
      `%c[W86:${label}] messages diff: ${(before || []).length} → ${(after || []).length} | +[${added.join(",")}] -[${removed.join(",")}]`,
      "color: #e67e22; font-weight: bold"
    );
  }
}

const LAYER_COLORS = {
  SSE: "#3498db",
  Transport: "#2ecc71",
  StreamStore: "#9b59b6",
  AgentStore: "#e74c3c",
  Chunks: "#f39c12",
  Render: "#1abc9c",
  Send: "#e91e63",
};

function log(layer, message, data) {
  if (!isEnabled()) return;
  const color = LAYER_COLORS[layer] || "#95a5a6";
  const n = seq();
  const prefix = `[W86:${layer}] #${n} ${ts()}`;
  if (data !== undefined) {
    console.log(`%c${prefix} ${message}`, `color: ${color}; font-weight: bold`, data);
  } else {
    console.log(`%c${prefix} ${message}`, `color: ${color}; font-weight: bold`);
  }
}

/**
 * Always-on logger for critical SSE lifecycle events. Emits regardless of
 * `w86_stream_debug` localStorage flag so connection drops / stuck states can
 * be diagnosed from the browser console without manual opt-in.
 *
 * Levels map to console methods:
 *   "info"  → console.log
 *   "warn"  → console.warn
 *   "error" → console.error
 */
function critical(layer, level, message, data) {
  const color = LAYER_COLORS[layer] || "#95a5a6";
  const n = seq();
  const prefix = `[W86:${layer}] #${n} ${ts()}`;
  const method = level === "error" ? "error" : level === "warn" ? "warn" : "log";
  const style = `color: ${color}; font-weight: bold`;
  if (data !== undefined) {
    console[method](`%c${prefix} ${message}`, style, data);
  } else {
    console[method](`%c${prefix} ${message}`, style);
  }
}

function logGroup(layer, message, fn) {
  if (!isEnabled()) return;
  const color = LAYER_COLORS[layer] || "#95a5a6";
  const n = seq();
  const prefix = `[W86:${layer}] #${n} ${ts()}`;
  console.groupCollapsed(`%c${prefix} ${message}`, `color: ${color}; font-weight: bold`);
  try {
    fn();
  } finally {
    console.groupEnd();
  }
}

function enable() {
  _enabled = true;
  _seq = 0;
  try { localStorage.setItem("w86_stream_debug", "1"); } catch {}
  console.log("%c[W86] Stream debug logging ENABLED. Reload to apply everywhere. Call w86debug.disable() to turn off.", "color: #2ecc71; font-weight: bold; font-size: 14px");
}

function disable() {
  _enabled = false;
  _seq = 0;
  try { localStorage.removeItem("w86_stream_debug"); } catch {}
  console.log("%c[W86] Stream debug logging DISABLED.", "color: #e74c3c; font-weight: bold; font-size: 14px");
}

function status() {
  console.log(`[W86] debug=${isEnabled()}, seq=${_seq}`);
}

if (typeof window !== "undefined") {
  window.w86debug = { enable, disable, status, seq: () => _seq };
}

export {
  isEnabled,
  log,
  critical,
  logGroup,
  summarizeMessages,
  summarizeParts,
  findIncompleteToolParts,
  summarizeIncompleteTools,
  diffMessages,
  enable,
  disable,
};
